/**
 * CAPI Replay Panel — backfill events from a rotated Pixel.
 *
 * When a Meta Pixel / Business Manager gets disabled, the operator
 * rotates to a new Pixel and starts from zero on Meta's side
 * (Meta does not allow merging events between Pixels). HitzLink
 * keeps a 90-day server-side ledger of clicks + postbacks, so within
 * Meta's 7-day `event_time` window we can re-fire the most recent
 * conversions into the new Pixel — instantly seeding it with
 * Custom Audiences, conversion-event eligibility, and a populated
 * Events Manager dashboard.
 *
 * UX flow
 * ───────
 *   1. Pick a SOURCE Pixel from a dropdown of "pixels with stored
 *      data" (we fetch from /api/capi/replay/sources). Each entry
 *      shows click count + most-recent-seen timestamp so the
 *      operator can identify "yes, this is the one I rotated away
 *      from".
 *   2. Pick a TARGET token from the account's existing token list
 *      (the same tokens managed by AccountCapiTokens above).
 *   3. (Optional) Paste a TEST EVENT CODE to route events to Meta's
 *      Test Events tab for verification before going live.
 *   4. Hit "Preview" → server returns plan summary (events by name,
 *      stale count, sample event). UI shows confirmation card.
 *   5. Hit "Run sync" → server fires events to Meta, returns
 *      execution summary (sent / failed / batches / errors).
 *
 * Hard guarantees we surface in the UI:
 *   • Idempotent: clicking Run twice doesn't double-count (server
 *     uses deterministic event_id, Meta dedupes).
 *   • Stale-event filter: events older than Meta's 7-day window are
 *     skipped; the count is shown so operators understand what got
 *     dropped and why.
 *   • Match quality is lower than original events — disclosed in a
 *     callout so operators don't expect identical attribution.
 */

import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  History,
  Loader2,
  Lock,
  Play,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useEffectivePlan } from '../../store/accountStore';
import { hasFeature } from '../../constants/planLimits';
import { openUpgradeGate } from '../../store/upgradeGateStore';

const API_URL = 'https://api.hitzlink.com/api';

function getAuthToken(): string {
  const fromStore = useAuthStore.getState().token;
  if (fromStore) return fromStore;
  return localStorage.getItem('token') || '';
}

interface ReplaySource {
  pixelId: string;
  clicks: number;
  postbacks: number;
  latestAt: string | null;
}

interface AccountTokenLite {
  id: string;
  name: string;
  pixelId: string;
}

interface PlanSummary {
  totalEvents: number;
  countsByName: Record<string, number>;
  skippedStale: number;
  restampedStale?: number;
  sampleEvent: unknown | null;
}

interface ExecutionSummary {
  sent: number;
  failed: number;
  batches: number;
  errors: Array<{ batchIndex: number; message: string; status?: number }>;
  durationMs: number;
}

/**
 * Locked-state card shown when the user's plan does not include the
 * `capiReplay` feature. Kept as a separate component so the main panel
 * doesn't have to mount any of its useState / useEffect hooks for users
 * who can't use the feature anyway — avoids wasted API calls and any
 * Rules-of-Hooks risk from gating mid-render.
 */
const CapiReplayLockedCard: React.FC<{ currentPlan: string | null | undefined }> = ({ currentPlan }) => (
  <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
    <div className="bg-gradient-to-br from-amber-50 via-white to-orange-50 px-6 py-8 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white mb-4">
        <Sparkles className="w-7 h-7" />
      </div>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wide">
        <Lock className="w-3 h-3" />
        Enterprise only
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        CAPI Replay (Sync Transfer Pixel)
      </h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto mb-5 leading-relaxed">
        Backfill the last 7 days of stored conversion events from a
        rotated Pixel into a freshly-added one. Recover Custom Audience
        material and conversion-event eligibility instantly after a
        BM/Pixel rotation — no manual re-warmup window.
      </p>
      <button
        type="button"
        onClick={() =>
          openUpgradeGate({
            feature: 'capiReplay',
            requiredPlan: 'enterprise',
            currentPlan: currentPlan,
            message:
              'Upgrade to Enterprise to backfill rotated Pixels with stored events.',
          })
        }
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold shadow-sm hover:shadow-md transition-shadow"
      >
        <Sparkles className="w-4 h-4" />
        Upgrade to Enterprise
      </button>
    </div>
  </div>
);

const CapiReplayPanelInner: React.FC = () => {
  const { t } = useTranslation('tracking');

  const [sources, setSources] = React.useState<ReplaySource[]>([]);
  const [tokens, setTokens] = React.useState<AccountTokenLite[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const [fromPixelId, setFromPixelId] = React.useState<string>('');
  const [toTokenId, setToTokenId] = React.useState<string>('');
  const [testEventCode, setTestEventCode] = React.useState<string>('');

  const [plan, setPlan] = React.useState<PlanSummary | null>(null);
  const [execution, setExecution] = React.useState<ExecutionSummary | null>(null);
  const [planning, setPlanning] = React.useState(false);
  const [executing, setExecuting] = React.useState(false);

  const loadAll = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const auth = `Bearer ${getAuthToken()}`;
      const [srcRes, tokRes] = await Promise.all([
        fetch(`${API_URL}/capi/replay/sources`, { headers: { Authorization: auth } }),
        fetch(`${API_URL}/auth/me/capi-settings`, { headers: { Authorization: auth } }),
      ]);
      if (srcRes.ok) {
        const body = await srcRes.json();
        setSources(Array.isArray(body.sources) ? body.sources : []);
      }
      if (tokRes.ok) {
        const body = await tokRes.json();
        const list = Array.isArray(body?.tokens) ? body.tokens : [];
        setTokens(
          list.map((t: { id: string; name: string; pixelId: string }) => ({
            id: t.id,
            name: t.name,
            pixelId: t.pixelId,
          }))
        );
      }
    } catch (err) {
      console.warn('[capi-replay] load error', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Auto-select sensible defaults: pick the top source (most recent)
  // and the first token whose pixelId differs from the source (the
  // common case — operator rotated to a NEW pixel). Falls back to
  // first token when no diff exists.
  React.useEffect(() => {
    if (!fromPixelId && sources.length > 0) {
      setFromPixelId(sources[0].pixelId);
    }
  }, [sources, fromPixelId]);

  React.useEffect(() => {
    if (!toTokenId && tokens.length > 0 && fromPixelId) {
      const candidate = tokens.find((t) => t.pixelId && t.pixelId !== fromPixelId) || tokens[0];
      if (candidate) setToTokenId(candidate.id);
    }
  }, [tokens, fromPixelId, toTokenId]);

  const targetToken = React.useMemo(
    () => tokens.find((t) => t.id === toTokenId) || null,
    [tokens, toTokenId]
  );

  const callReplay = React.useCallback(
    async (dryRun: boolean) => {
      if (!fromPixelId || !toTokenId) {
        toast.error(t('replay.errMissingSelection'));
        return;
      }
      if (dryRun) setPlanning(true);
      else setExecuting(true);
      try {
        const res = await fetch(`${API_URL}/capi/replay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({
            fromPixelId,
            toTokenId,
            testEventCode: testEventCode.trim() || undefined,
            dryRun,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body?.ok) {
          toast.error(body?.error || t('replay.errFailed'));
          return;
        }
        setPlan(body.plan || null);
        setExecution(dryRun ? null : body.execution || null);
        if (!dryRun) {
          if ((body.execution?.failed ?? 0) > 0) {
            toast.success(t('replay.toastPartial'));
          } else if ((body.execution?.sent ?? 0) > 0) {
            toast.success(t('replay.toastDone'));
          } else {
            toast(t('replay.toastNothing'));
          }
        }
      } catch (err) {
        const e = err as Error;
        toast.error(e.message || t('replay.errFailed'));
      } finally {
        setPlanning(false);
        setExecuting(false);
      }
    },
    [fromPixelId, toTokenId, testEventCode, t]
  );

  const sameSourceAndTarget =
    !!fromPixelId && !!targetToken?.pixelId && fromPixelId === targetToken.pixelId;

  return (
    <div className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/40 via-slate-950/40 to-slate-950/40 p-5 text-slate-200">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
            <History className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">{t('replay.title')}</h3>
            <p className="mt-0.5 text-xs text-slate-400">{t('replay.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadAll}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800/40 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
          disabled={refreshing}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {t('replay.refresh')}
        </button>
      </header>

      {/* Disclosure callout — sets expectations BEFORE the operator
          configures anything. Important: replay is best-effort; we
          want the operator to understand the trade-offs upfront. */}
      <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
        <div className="mb-1 flex items-center gap-1.5 font-semibold text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" />
          {t('replay.calloutTitle')}
        </div>
        <ul className="ml-5 list-disc space-y-0.5 text-amber-200/90">
          <li>{t('replay.calloutLimit7d')}</li>
          <li>{t('replay.calloutMatchQuality')}</li>
          <li>{t('replay.calloutIdempotent')}</li>
        </ul>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-sm text-slate-400">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('replay.loading')}
        </div>
      ) : sources.length === 0 ? (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-4 text-center text-sm text-slate-400">
          {t('replay.noSources')}
        </div>
      ) : (
        <>
          {/* Source / target picker grid */}
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">
                {t('replay.sourceLabel')}
              </label>
              <select
                value={fromPixelId}
                onChange={(e) => {
                  setFromPixelId(e.target.value);
                  setPlan(null);
                  setExecution(null);
                }}
                className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:border-emerald-500 focus:outline-none"
              >
                {sources.map((s) => (
                  <option key={s.pixelId} value={s.pixelId}>
                    {s.pixelId} · {t('replay.optClicks', { n: s.clicks })}
                    {s.postbacks > 0 ? ` · ${t('replay.optPostbacks', { n: s.postbacks })}` : ''}
                    {s.latestAt
                      ? ` · ${t('replay.optLatest', { when: new Date(s.latestAt).toLocaleString() })}`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-300">
                {t('replay.targetLabel')}
              </label>
              <select
                value={toTokenId}
                onChange={(e) => {
                  setToTokenId(e.target.value);
                  setPlan(null);
                  setExecution(null);
                }}
                className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 focus:border-emerald-500 focus:outline-none"
                disabled={tokens.length === 0}
              >
                {tokens.length === 0 && (
                  <option value="">{t('replay.noTokens')}</option>
                )}
                {tokens.map((tk) => (
                  <option key={tk.id} value={tk.id}>
                    {tk.name} {tk.pixelId ? `(Pixel ${tk.pixelId})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Test event code (optional) */}
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-slate-300">
              {t('replay.testCodeLabel')}
            </label>
            <input
              type="text"
              value={testEventCode}
              onChange={(e) => setTestEventCode(e.target.value)}
              placeholder="TEST00000 (optional)"
              className="w-full rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-500">{t('replay.testCodeHint')}</p>
          </div>

          {/* Same-pixel guard — surface BEFORE buttons so operator
              fixes selection rather than firing a useless replay. */}
          {sameSourceAndTarget && (
            <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
              {t('replay.errSamePixel')}
            </div>
          )}

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => callReplay(true)}
              disabled={planning || executing || sameSourceAndTarget}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-600 bg-slate-800/60 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />}
              {t('replay.btnPreview')}
            </button>
            <button
              type="button"
              onClick={() => callReplay(false)}
              disabled={
                planning ||
                executing ||
                sameSourceAndTarget ||
                !plan ||
                plan.totalEvents === 0
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {executing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {t('replay.btnRun')}
            </button>
            {!!plan && (
              <span className="text-xs text-slate-400">
                {t('replay.btnRunHint', { n: plan.totalEvents })}
              </span>
            )}
          </div>

          {/* Plan summary card */}
          {plan && (
            <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <h4 className="mb-2 text-sm font-semibold text-white">{t('replay.planTitle')}</h4>
              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                <Stat label={t('replay.statTotal')} value={plan.totalEvents} />
                {Object.entries(plan.countsByName).map(([name, n]) => (
                  <Stat key={name} label={name} value={n} />
                ))}
                {(plan.restampedStale ?? 0) > 0 && (
                  <Stat
                    label={t('replay.statRestamped', { defaultValue: 'ปรับเวลาเป็นล่าสุด (>7 วัน)' })}
                    value={plan.restampedStale ?? 0}
                    tone="ok"
                  />
                )}
                {plan.skippedStale > 0 && (
                  <Stat
                    label={t('replay.statSkipped')}
                    value={plan.skippedStale}
                    tone="warn"
                  />
                )}
              </div>
            </div>
          )}

          {/* Execution result card */}
          {execution && (
            <div
              className={`mt-3 rounded-lg border p-4 ${
                execution.failed > 0
                  ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-emerald-500/40 bg-emerald-500/5'
              }`}
            >
              <div className="mb-2 flex items-center gap-2">
                <CheckCircle2
                  className={`h-4 w-4 ${execution.failed > 0 ? 'text-amber-400' : 'text-emerald-400'}`}
                />
                <h4 className="text-sm font-semibold text-white">{t('replay.execTitle')}</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                <Stat label={t('replay.statSent')} value={execution.sent} tone="ok" />
                {execution.failed > 0 && (
                  <Stat label={t('replay.statFailed')} value={execution.failed} tone="warn" />
                )}
                <Stat label={t('replay.statBatches')} value={execution.batches} />
                <Stat
                  label={t('replay.statDuration')}
                  value={`${(execution.durationMs / 1000).toFixed(1)}s`}
                />
              </div>
              {execution.errors.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-amber-300 hover:text-amber-200">
                    {t('replay.errorsToggle', { n: execution.errors.length })}
                  </summary>
                  <ul className="mt-2 space-y-1 rounded-md border border-slate-700 bg-slate-900/60 p-2 font-mono text-amber-200/80">
                    {execution.errors.slice(0, 20).map((e, i) => (
                      <li key={i}>
                        batch #{e.batchIndex} — {e.message}
                        {e.status ? ` (HTTP ${e.status})` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{
  label: string;
  value: number | string;
  tone?: 'ok' | 'warn' | 'default';
}> = ({ label, value, tone }) => {
  const valueClass =
    tone === 'ok'
      ? 'text-emerald-300'
      : tone === 'warn'
      ? 'text-amber-300'
      : 'text-white';
  return (
    <div className="rounded-md border border-slate-700/50 bg-slate-900/60 px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
};

/**
 * Public wrapper. Branches on the `capiReplay` plan feature so the
 * inner panel (with its API hooks) only mounts for Enterprise users.
 * Non-Enterprise users see a locked card explaining what they'd unlock.
 */
export const CapiReplayPanel: React.FC = () => {
  const effectivePlan = useEffectivePlan();
  if (!hasFeature(effectivePlan, 'capiReplay')) {
    return <CapiReplayLockedCard currentPlan={effectivePlan} />;
  }
  return <CapiReplayPanelInner />;
};
